import React from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { FiClock, FiActivity } from 'react-icons/fi';

const Sparkline = ({ data, color }) => {
  const safe = Array.isArray(data) ? data : [];
  if (safe.length === 0) return <div style={{ width: 80, height: 32 }} />;
  const chartData = safe.map((v, i) => ({ v, i }));
  return (
    <div style={{ width: 80, height: 32 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 2 }}>
          <defs>
            <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            fill={`url(#spark-${color.replace('#', '')})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const Card = ({ icon, iconBg, title, value, unit, sparkline, sparkColor, insight, grow = false }) => (
  // When grown vertically (stacked layout next to a tall sibling), spread
  // the three sections (header, big number, insight) so the card looks
  // intentional instead of leaving a dead zone at the bottom.
  <div className={`bg-white shadow-sm rounded-xl p-4 border border-gray-100 flex flex-col gap-2 min-w-0 ${grow ? 'flex-1 justify-between' : ''}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <div className={`p-1.5 rounded-lg ${iconBg}`}>{icon}</div>
        <span className="text-xs font-medium text-gray-500 truncate">{title}</span>
      </div>
      {sparkline && <Sparkline data={sparkline} color={sparkColor} />}
    </div>
    <div className="flex items-end gap-1.5">
      <span className="text-3xl font-bold text-gray-900 leading-none">{value}</span>
      {unit && <span className="text-xs text-gray-400 mb-1">{unit}</span>}
    </div>
    {insight && <p className="text-xs text-gray-500 leading-relaxed">{insight}</p>}
  </div>
);

const VelocityCards = ({ data, stacked = false }) => {
  const safe = data || { reactionTime: {}, cadence: {} };
  const reaction = safe.reactionTime || {};
  const cadence = safe.cadence || {};
  // When `stacked`, render the two cards vertically and let each card
  // expand to fill the parent column's height (useful when sitting next
  // to a taller panel like the Sankey). The default layout keeps them
  // side-by-side.
  const wrapperCls = stacked
    ? 'flex flex-col gap-4 h-full'
    : 'grid grid-cols-1 sm:grid-cols-2 gap-4';
  return (
    <div className={wrapperCls}>
      <Card
        icon={<FiClock className="h-4 w-4 text-blue-600" />}
        iconBg="bg-blue-50"
        title="Reaction Time"
        value={reaction.currentMedian ?? 0}
        unit="days avg"
        sparkline={reaction.trend}
        sparkColor="#3b82f6"
        insight={reaction.insight}
        grow={stacked}
      />
      <Card
        icon={<FiActivity className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Application Cadence"
        value={cadence.thisWeek ?? 0}
        unit="this week"
        sparkline={cadence.trend}
        sparkColor="#5AAFA7"
        insight={cadence.insight}
        grow={stacked}
      />
    </div>
  );
};

export default VelocityCards;
